import { useState, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { DashboardService } from '../services/dashboard';
import { GenericGraphicBarData } from '../components/UIelements/GenericGraphicBar';
import { PeakUsage } from '../models/DashboardDetails';

const WEEK_DAYS = [
  { id: 'seg', labelId: 'equipment.seg' },
  { id: 'ter', labelId: 'equipment.ter' },
  { id: 'qua', labelId: 'equipment.qua' },
  { id: 'qui', labelId: 'equipment.qui' },
  { id: 'sex', labelId: 'equipment.sex' },
  { id: 'sab', labelId: 'equipment.sab' },
  { id: 'dom', labelId: 'equipment.dom' },
] as const;

const COLORS = ['#2F6BB2', '#A070E0', '#51B3D6', '#26496E', '#D651A1', '#4F6E89'];

type Props = {
  scopeType: 'group' | 'location';
  scopeId?: number;
};

export const usePeakUsage = ({ scopeType, scopeId }: Props) => {
  const intl = useIntl();
  const [peakUsageData, setPeakUsageData] = useState<PeakUsage.List>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!scopeId) return;

    let active = true;

    const fetchStats = async () => {
      setLoading(true);

      try {
        const apiScopeType = scopeType === 'group' ? 'STORE' : 'LOCATION';
        
        const response = await DashboardService.getPeakUsage({ 
          scopeType: apiScopeType, 
          scopeId
        });

        if (active) setPeakUsageData(response.peakUsage);

      } catch (err) {
        console.error(err);
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchStats();

    return () => { active = false; };
  }, [scopeType, scopeId]);

  const chartData = useMemo<GenericGraphicBarData | null>(() => {
    if (loading || peakUsageData.length === 0) return null;

    const uniqueEquipments = Array.from(new Set(peakUsageData.map(d => d.equipmentId)))
      .sort((a, b) => a - b);
    
    const colorMap = new Map();
    uniqueEquipments.forEach((id, index) => {
      colorMap.set(id, COLORS[index % COLORS.length]);
    });

    const nameMap = new Map();
    peakUsageData.forEach(d => nameMap.set(d.equipmentId, d.equipmentName));

    return {
      axis: {
        xLabel: intl.formatMessage({ id: 'equipment.Dias da semana' }),
        yLabel: intl.formatMessage({ id: 'equipment.Horário 24h' }),
        maxValue: 24,
      },
      groupedDataset: WEEK_DAYS.map(day => ({
        id: day.id,
        label: intl.formatMessage({ id: day.labelId }),
        values: uniqueEquipments.map(eqId => {
          const found = peakUsageData.find(d => d.dayOfWeek === day.id && d.equipmentId === eqId);
          
          return {
            id: `equipment-${eqId}`,
            label: nameMap.get(eqId) ?? intl.formatMessage({ id: 'equipment.Desconhecido' }),
            value: found ? found.peakHour : 0,
            color: colorMap.get(eqId),
          };
        })
      }))
    };
  }, [peakUsageData, loading, intl]);

  return {
    chartData,
    loading
  };
};
